import { inject, Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { from, of, switchMap } from 'rxjs';

import { DatadogLogger } from '@ninety/mobile/platform/services';

import { appActions } from '../../app/app.actions';
import { BaseEffects } from '../../base.effects';
import { CurrentPersonStateActions } from '../current-person/current-person-state.actions';
import { CurrentPersonSelectors } from '../current-person/current-person-state.selectors';
import { SessionActions } from '../session/session.actions';

import { OneSignalActions } from './one-signal.actions';
import { OneSignalService } from './one-signal.service';

@Injectable()
export class OneSignalEffects extends BaseEffects {
  private oneSignalService = inject(OneSignalService);
  private logger = inject(DatadogLogger);

  /* Initialize OneSignal on app init */
  appInit_oneSignalInit$ = this.mapEffect({
    action: appActions.initGlobalState,
    mapFn: () => OneSignalActions.initialize(),
  });

  initializeOneSignal$ = this.tapEffect({
    action: OneSignalActions.initialize,
    tapFn: () => this.oneSignalService.initialize(),
  });

  /* Prompt on every launch that resolves a signed-in person. `getLoginPersonSuccess` follows
     `initGlobalState`, so a restored session reaches it too, not just a fresh sign-in.

     This is the only prompt trigger, so it cannot afford to fire before the plugin is ready.
     Ordering is enforced in `OneSignalService.requestPermission`, which awaits init itself rather
     than trusting that the effect above finished first. */
  checkForOneSignalAtUserLogin$ = this.mapEffect({
    action: CurrentPersonStateActions.getLoginPersonSuccess,
    mapFn: () => OneSignalActions.requestPermission(),
  });

  initiateOneSignalPrompt$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OneSignalActions.requestPermission),
        concatLatestFrom(() => [this.store.select(CurrentPersonSelectors.person)]),
        switchMap(([_, person]) => {
          if (!person) {
            // No user is logged in, no need to prompt.
            return of(null);
          }
          return from(this.oneSignalService.requestPermission(person));
        })
      ),
    { dispatch: false }
  );

  /* The user asked to be prompted from Notification Settings — bypasses the automatic backoff. */
  promptFromSettings$ = this.tapWithConcatEffect({
    action: OneSignalActions.promptFromSettings,
    concatLatestFromFn: () => this.store.select(CurrentPersonSelectors.person),
    tapFn: (_, person) => {
      if (person) this.oneSignalService.promptFromSettings(person);
    },
  });

  /**
   * The user fixed permission in device settings rather than through our prompt, so no login ran.
   * Without this, push stays broken until the next app launch happens to hit the already-granted branch.
   */
  ensurePersonLoggedIn$ = this.tapWithConcatEffect({
    action: OneSignalActions.ensurePersonLoggedIn,
    concatLatestFromFn: () => this.store.select(CurrentPersonSelectors.person),
    tapFn: (_, person) => {
      if (!person) return;
      // `login` is also called by the automatic flow's already-granted branch and by the
      // settings prompt's accept branch, and logs identically for all three. Without this the
      // recovery path can't be told apart in Datadog — and a silent failure here looks exactly
      // like the bug this ticket fixes.
      this.logger.info({
        message: 'OneSignal: Ensuring person logged in — permission granted outside our prompt',
        data: { personId: person._id },
      });
      this.oneSignalService.login(person._id);
    },
  });

  logoutOneSignal$ = this.tapEffect({
    action: SessionActions.signedOut,
    tapFn: () => this.oneSignalService.logout(),
  });
}
